import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MerchantOperationRepository } from '@app/domain/ports/tpv/merchant-operation.repository';
import { TpvMerchantOperationResult } from '@app/domain/models/tpv/merchant-operation.model';
import {
  CreateMerchantOperationRequest,
  UpdateMerchantOperationRequest,
} from '@app/data/dtos/tpv/merchant-operation.dto';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/**
 * CRUD de datos de operación de un comercio (`/merchants/<id>/operation/`, §3):
 * POST alta, PATCH parcial y DELETE. Auth y x-api-key vía interceptores
 * estándar; `Idempotency-Key` opcional en las escrituras con body.
 */
@Injectable()
export class MerchantOperationRemoteDataSource extends MerchantOperationRepository {
  private readonly http = inject(HttpClient);

  override getOperation(externalIdMerchant: string): Observable<TpvMerchantOperationResult> {
    return this.http
      .get<TpvEnvelope<unknown>>(
        tpvUrl(`merchants/${encodeURIComponent(externalIdMerchant)}/operation/`),
      )
      .pipe(
        map((raw) => ({
          idMerchant: externalIdMerchant,
          data: unwrapTpv<unknown>(raw),
        })),
      );
  }

  override createOperation(
    externalIdMerchant: string,
    request: CreateMerchantOperationRequest,
    idempotencyKey?: string,
  ): Observable<TpvMerchantOperationResult> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }
    return this.http
      .post<TpvEnvelope<unknown>>(
        tpvUrl(`merchants/${encodeURIComponent(externalIdMerchant)}/operation/`),
        request,
        { headers },
      )
      .pipe(
        map((raw) => ({
          idMerchant: externalIdMerchant,
          data: unwrapTpv<unknown>(raw),
        })),
      );
  }

  override updateOperation(
    externalIdMerchant: string,
    request: UpdateMerchantOperationRequest,
    idempotencyKey?: string,
  ): Observable<TpvMerchantOperationResult> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }
    return this.http
      .patch<TpvEnvelope<unknown>>(
        tpvUrl(`merchants/${encodeURIComponent(externalIdMerchant)}/operation/`),
        request,
        { headers },
      )
      .pipe(
        map((raw) => ({
          idMerchant: externalIdMerchant,
          data: unwrapTpv<unknown>(raw),
        })),
      );
  }

  override deleteOperation(externalIdMerchant: string): Observable<void> {
    return this.http
      .delete<TpvEnvelope<unknown>>(
        tpvUrl(`merchants/${encodeURIComponent(externalIdMerchant)}/operation/`),
      )
      .pipe(map(() => undefined));
  }
}
